import React, { useContext, useEffect, useState } from 'react';
import { Alert, Button } from '@mui/material';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import InstallDesktopIcon from '@mui/icons-material/InstallDesktop';
import { useOffline } from './OfflineProvider';
import { OFFLINE_ENABLED, OFFLINE_AVAILABLE, isOfflineOptIn } from './entities';
import {
  isStandalone,
  canPromptInstall,
  promptInstall,
  wasInstalledOnDevice,
  subscribeInstallAvailability,
} from './installPrompt';
import { ConflictReviewDialog } from './ConflictReviewDialog';
import { AuthContext } from '../auth/AuthContext';
import { useFormatInstant } from '../hooks/useClinicTimezone';
import { INSTANT_FORMATS } from '../utils/dateUtils';

/**
 * SETUP NUDGES — the two halves of the recommended offline setup (turn Offline
 * mode on, then use the installed app). Opt-in and the installed app share the
 * same on-device storage, so EITHER ORDER works; each nudge steers toward
 * whichever half is missing, and both are permanently dismissible:
 *  - opted-in but browsing in a TAB  -> "use the app" nudge
 *  - in the APP but not opted-in     -> "turn on Offline mode" nudge
 *  - tab + not opted-in              -> nothing (don't pester non-users)
 *  - app + opted-in                  -> nothing (the ideal state)
 */
const INSTALL_NUDGE_DISMISSED_KEY = 'st.offline.installNudgeDismissed';
const ENABLE_NUDGE_DISMISSED_KEY = 'st.offline.enableNudgeDismissed';

/** B12: how old an offline replica may be before the indicator escalates from a
 *  soft "showing cached data" to a loud "N days old — reconnect" warning. */
export const STALE_AFTER_DAYS = 7;
const STALE_MS = STALE_AFTER_DAYS * 24 * 60 * 60 * 1000;

/**
 * Always-visible status when offline OR when there are unsynced/parked offline
 * writes. Invisible in the normal online, all-synced case. Surfaces the last-sync
 * time, the unsynced count, and a "Review" button to resolve conflicts/failures.
 */
export const OfflineIndicator: React.FC = () => {
  const {
    isOnline,
    lastSyncAt,
    unsyncedCount,
    conflictCount,
    failedCount,
    persisted,
    storageFull,
    initialSyncDone,
  } = useOffline();
  const { graceMode } = useContext(AuthContext);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [installNudgeDismissed, setInstallNudgeDismissed] = useState(() => {
    try {
      return localStorage.getItem(INSTALL_NUDGE_DISMISSED_KEY) === 'true';
    } catch {
      return true;
    }
  });
  const [enableNudgeDismissed, setEnableNudgeDismissed] = useState(() => {
    try {
      return localStorage.getItem(ENABLE_NUDGE_DISMISSED_KEY) === 'true';
    } catch {
      return true;
    }
  });
  const [, setInstallTick] = useState(0);
  useEffect(() => subscribeInstallAvailability(() => setInstallTick((t) => t + 1)), []);
  // `lastSyncAt` is an INSTANT (the replica's `lastSyncTimestamp` meta row — a
  // real moment the sync finished), so it renders in the CLINIC'S zone. The
  // hook itself is offline-capable: it reads the synced clinic row when there
  // is no network, and falls back to the schema default otherwise.
  const formatInstant = useFormatInstant();

  // Flag-off builds render NOTHING (byte-identical to the pre-offline app).
  if (!OFFLINE_AVAILABLE) return null;

  const dismissInstallNudge = () => {
    try {
      localStorage.setItem(INSTALL_NUDGE_DISMISSED_KEY, 'true');
    } catch {
      /* localStorage unavailable — session-only dismiss */
    }
    setInstallNudgeDismissed(true);
  };
  const dismissEnableNudge = () => {
    try {
      localStorage.setItem(ENABLE_NUDGE_DISMISSED_KEY, 'true');
    } catch {
      /* session-only */
    }
    setEnableNudgeDismissed(true);
  };
  const handleInstallClick = async () => {
    const outcome = await promptInstall();
    if (outcome === 'accepted') dismissInstallNudge();
  };

  const standalone = isStandalone();
  const optedIn = isOfflineOptIn(); // live read — survives the frozen-at-load gate
  const appKnownInstalled = wasInstalledOnDevice();
  const showUseAppNudge = OFFLINE_ENABLED && optedIn && !standalone && !installNudgeDismissed;
  const showEnableNudge = !optedIn && standalone && !enableNudgeDismissed;

  // With the engine off (not opted in), only the enable-nudge can apply.
  if (!OFFLINE_ENABLED && !showEnableNudge) return null;
  const needsReview = conflictCount + failedCount;
  // B9: the first full download is still in progress.
  const downloading = isOnline && !initialSyncDone;
  // Durability advisory: persistent storage not granted, so offline data COULD
  // be evicted under disk pressure. Writes are NOT blocked on this (see
  // executeWrite) — and the warning only shows when it's ACTIONABLE: in a
  // BROWSER TAB (where "use the app" is the remedy), while offline or with
  // un-synced work at risk. In the INSTALLED app it never shows — the user has
  // already done everything we could ask, the app syncs automatically on
  // reconnect, and an installed PWA's eviction risk is the minimum achievable;
  // a warning with no available action is just fear. (null = still checking.)
  const notDurable =
    persisted === false && !standalone && (!isOnline || unsyncedCount > 0);
  // B12: how stale is the replica? Only meaningful while offline (online, sync()
  // is actively refreshing and any failure already shows via unsynced/review).
  const ageMs = !isOnline && lastSyncAt ? Date.now() - new Date(lastSyncAt).getTime() : 0;
  const ageDays = Math.floor(ageMs / 86_400_000);
  const isStale = ageMs >= STALE_MS;
  const showSyncBanner =
    OFFLINE_ENABLED && (!isOnline || unsyncedCount > 0 || needsReview > 0 || graceMode);
  if (
    !showSyncBanner &&
    !notDurable &&
    !storageFull &&
    !downloading &&
    !showUseAppNudge &&
    !showEnableNudge
  ) {
    return null;
  }

  const parts: string[] = [];
  if (!isOnline) {
    parts.push(
      lastSyncAt
        ? `last synced ${formatInstant(lastSyncAt, INSTANT_FORMATS.dateTimeNumeric)}`
        : 'not yet synced',
    );
  }
  if (unsyncedCount > 0) parts.push(`${unsyncedCount} unsynced`);
  if (conflictCount > 0) parts.push(`${conflictCount} need review`);
  if (failedCount > 0) parts.push(`${failedCount} failed to save`);

  const severity =
    needsReview > 0 || isStale ? 'error' : !isOnline || graceMode ? 'warning' : 'info';
  // A5: in grace, the cached session is being trusted offline — tell the vet
  // their work is saved and they'll re-authenticate to sync on reconnect.
  // (Cause-agnostic: the trigger may be a cross-tab sign-out / eviction, not an
  // "expiry" — a purely-offline token expiry does not end the session.)
  const lead = graceMode
    ? 'Working offline on a cached session — your work is saved; sign in to sync when reconnected'
    : !isOnline
    ? isStale
      ? `Cached data is ${ageDays} days old — reconnect to refresh before relying on it`
      : 'Offline — showing cached data'
    : 'Syncing changes';

  return (
    <>
      {notDurable && (
        <Alert
          severity="warning"
          sx={{ borderRadius: 0, py: 0.5, '& .MuiAlert-message': { fontSize: 13 } }}
        >
          Offline data isn't fully secured on this device — it may be cleared by the browser, so
          sync as soon as you reconnect. For the most reliable offline storage, use the StableTrack
          app.
        </Alert>
      )}
      {storageFull && (
        <Alert
          severity="error"
          sx={{ borderRadius: 0, py: 0.5, '& .MuiAlert-message': { fontSize: 13 } }}
        >
          This device is out of storage — offline data couldn't finish downloading and new changes
          can't be saved offline. Free up space, then sync while online.
        </Alert>
      )}
      {downloading && (
        <Alert
          severity="info"
          sx={{ borderRadius: 0, py: 0.5, '& .MuiAlert-message': { fontSize: 13 } }}
        >
          Downloading clinic data for offline use… you can keep working; offline access will be
          ready shortly.
        </Alert>
      )}
      {showUseAppNudge && (
        <Alert
          severity="info"
          icon={<InstallDesktopIcon fontSize="inherit" />}
          sx={{ borderRadius: 0, py: 0.5, '& .MuiAlert-message': { fontSize: 13 } }}
          action={
            <>
              {canPromptInstall() && (
                <Button color="inherit" size="small" variant="outlined" onClick={() => void handleInstallClick()} sx={{ mr: 1 }}>
                  Use the app
                </Button>
              )}
              <Button color="inherit" size="small" onClick={dismissInstallNudge}>
                Not now
              </Button>
            </>
          }
        >
          {appKnownInstalled
            ? 'You have the StableTrack app on this device — use it for the better experience: its own window, and it opens even with no signal.'
            : canPromptInstall()
            ? 'For the better experience, use the StableTrack app — its own window, the most reliable offline storage, and it opens even with no signal.'
            : 'For the better experience, use the StableTrack app: open your browser menu and choose “Install StableTrack”.'}
        </Alert>
      )}
      {showEnableNudge && (
        <Alert
          severity="info"
          icon={<InstallDesktopIcon fontSize="inherit" />}
          sx={{ borderRadius: 0, py: 0.5, '& .MuiAlert-message': { fontSize: 13 } }}
          action={
            <Button color="inherit" size="small" onClick={dismissEnableNudge}>
              Not now
            </Button>
          }
        >
          Turn on Offline mode (Settings → Offline mode) to use StableTrack in the field — your
          clinic's records download to this device and everything syncs when you reconnect.
        </Alert>
      )}
      {showSyncBanner && (
        <Alert
          severity={severity}
          icon={<CloudOffIcon fontSize="inherit" />}
          action={
            needsReview > 0 ? (
              <Button color="inherit" size="small" onClick={() => setReviewOpen(true)}>
                Review
              </Button>
            ) : undefined
          }
          sx={{ borderRadius: 0, py: 0.5, '& .MuiAlert-message': { fontSize: 13 } }}
        >
          {lead}
          {parts.length ? ` · ${parts.join(' · ')}` : ''}
        </Alert>
      )}
      <ConflictReviewDialog open={reviewOpen} onClose={() => setReviewOpen(false)} />
    </>
  );
};
